/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   tenantLicenseService.ts
 *     Description     :   Spec 285 — client for the SuperAdmin-only tenant license admin API.
 *                         Goes through the authenticated apiClient (a raw fetch carries no
 *                         token and 401s — the Spec 259 trap). Every call addresses ONE tenant.
 *                         🛑 Read-only since the Spec 286 cut-over (FR-003, T064): FCM writes the
 *                         licence; the interim PUT is removed.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import { apiClient } from './apiClient';

/** One tenant's licence as FCM wrote it; status and tier are null only when the tenant has no licence row. */
export interface TenantLicenseRecord {
  platform_tenant_id: string;
  license_status: string | null;
  usage_tier: string | null;
  limit_workspaces: number | null;
  limit_items: number | null;
  limit_capacities: number | null;
  backup_enabled: boolean;
  backup_until: string | null;
  backup_in_force: boolean;
  ops_enabled: boolean;
  ops_until: string | null;
  ops_in_force: boolean;
  growth_frozen_from: string | null;
  source: string;
  updated_by: string | null;
  updated_at: string | null;
}

const BASE = '/api/admin/tenant-licenses';

export const tenantLicenseService = {
  /** The SuperAdmin's current tenant (resolved server-side from the session). */
  async getCurrent(): Promise<TenantLicenseRecord> {
    const { data } = await apiClient.get<TenantLicenseRecord>(`${BASE}/current`);
    return data;
  },

  /** One specific tenant, by id. */
  async get(tenantId: string): Promise<TenantLicenseRecord> {
    const { data } = await apiClient.get<TenantLicenseRecord>(`${BASE}/${encodeURIComponent(tenantId)}`);
    return data;
  },
};
